/**
 * Seções da integração TiFlux (v12, admin-only) — renderizadas num único
 * ponto da aba Dados de /settings/integrations (o "Gerenciar" da tabela e o
 * card-logo só alternam o expand): form endpoint/token/ativado/intervalo +
 * bloco de status da última sincronização (contadores, erros em dialog) e
 * botão "Sincronizar agora" (202 → poll do GET a cada 3s até lastFinishedAt
 * mudar ou 60s; 409 → toast).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Unlink } from 'lucide-react';
import { ClientPicker, type ClientRef } from '@/features/tickets/ClientPicker';
import { Badge } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input, Label, Select } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useTransientToast } from '@/components/ui/transient-toast';
import {
  TIFLUX_DEFAULT_ENDPOINT,
  TIFLUX_ENTITY_LABELS,
  TIFLUX_INTERVAL_OPTIONS,
  TIFLUX_KEY,
  TIFLUX_OPERATED_INTERVAL_OPTIONS,
  isTifluxConfigured,
  tifluxErpClientPreviewOptions,
  tifluxErpClientsOptions,
  tifluxErrorMessage,
  tifluxRelativeTime,
  useDetachTifluxErpClient,
  useTifluxSyncNow,
  useUpdateTifluxIntegration,
  type TifluxIntegration,
  type TifluxSyncError,
} from './tiflux';
import { Section } from './section';

/** Janela máxima do poll pós "Sincronizar agora" — o GET expõe `syncRunning`
 *  só do processo local, então além dele paramos quando lastFinishedAt muda ou
 *  ao estourar a janela. */
const SYNC_POLL_TIMEOUT_MS = 60_000;
const SYNC_POLL_INTERVAL_MS = 3_000;

export function TifluxSections({ integration }: { integration: TifluxIntegration }) {
  const configured = isTifluxConfigured(integration);
  return (
    <div className="space-y-4">
      <ConnectionSection integration={integration} />
      {configured ? <SyncSection integration={integration} /> : null}
      {configured ? <ErpClientsSection integration={integration} /> : null}
      {configured ? <HideMirroredSection integration={integration} /> : null}
    </div>
  );
}

// ── Conexão (endpoint / token / ativado / intervalo) ────────────────────────

function ConnectionSection({ integration }: { integration: TifluxIntegration }) {
  const configured = isTifluxConfigured(integration);
  const [endpoint, setEndpoint] = useState(integration.endpoint || TIFLUX_DEFAULT_ENDPOINT);
  const [token, setToken] = useState('');
  const [enabled, setEnabled] = useState(integration.enabled);
  const [intervalMinutes, setIntervalMinutes] = useState(integration.intervalMinutes);
  const [saved, setSaved] = useState(false);

  // Refetch fora do submit não deve sobrescrever o form em digitação —
  // re-sincroniza (durante o render, sem efeito) só quando os valores de que
  // o servidor é dono realmente mudam.
  const [prevServer, setPrevServer] = useState({
    endpoint: integration.endpoint,
    enabled: integration.enabled,
    intervalMinutes: integration.intervalMinutes,
  });
  if (
    prevServer.endpoint !== integration.endpoint ||
    prevServer.enabled !== integration.enabled ||
    prevServer.intervalMinutes !== integration.intervalMinutes
  ) {
    setPrevServer({
      endpoint: integration.endpoint,
      enabled: integration.enabled,
      intervalMinutes: integration.intervalMinutes,
    });
    setEndpoint(integration.endpoint || TIFLUX_DEFAULT_ENDPOINT);
    setEnabled(integration.enabled);
    setIntervalMinutes(integration.intervalMinutes);
  }

  const save = useUpdateTifluxIntegration();

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 3000);
    return () => clearTimeout(t);
  }, [saved]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmedToken = token.trim();
    // Primeira configuração exige o token (a API responde token_required);
    // depois dá para alterar só endpoint/ativado/intervalo.
    if (!configured && !trimmedToken) return;
    save.mutate(
      {
        endpoint: endpoint.trim() || TIFLUX_DEFAULT_ENDPOINT,
        ...(trimmedToken ? { token: trimmedToken } : {}),
        enabled,
        intervalMinutes,
      },
      {
        onSuccess: () => {
          setToken('');
          setSaved(true);
        },
      }
    );
  };

  return (
    <Section
      title="Conexão"
      description="Endpoint e token da conta TiFlux, ativação e intervalo da sincronização automática."
    >
      <form onSubmit={submit} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="tiflux-endpoint">Endpoint</Label>
            <Input
              id="tiflux-endpoint"
              type="url"
              autoComplete="off"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder={TIFLUX_DEFAULT_ENDPOINT}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tiflux-token">
              Token {configured ? null : <span className="text-destructive">*</span>}
            </Label>
            <Input
              id="tiflux-token"
              type="password"
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder={
                configured && integration.tokenMasked
                  ? `Configurado (${integration.tokenMasked}) — cole para trocar`
                  : 'Token da API do TiFlux'
              }
            />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="tiflux-interval">Intervalo de sincronização</Label>
            <Select
              id="tiflux-interval"
              value={String(intervalMinutes)}
              onChange={(e) => setIntervalMinutes(Number(e.target.value))}
            >
              {TIFLUX_INTERVAL_OPTIONS.map((min) => (
                <option key={min} value={min}>
                  A cada {min} minutos
                </option>
              ))}
            </Select>
          </div>
          <div className="flex items-end pb-1.5">
            <span className="flex items-center gap-2">
              <Switch
                id="tiflux-enabled"
                aria-label="Sincronização TiFlux ativada"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
              <Label htmlFor="tiflux-enabled" className="cursor-pointer">
                Sincronização ativada
              </Label>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="submit"
            size="sm"
            disabled={save.isPending || (!configured && token.trim() === '')}
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
          {saved ? (
            <span className="flex items-center gap-1 text-xs text-[#52C41A]">
              <CheckCircle2 className="size-3.5" />
              Integração salva.
            </span>
          ) : null}
          {save.error ? (
            <span className="text-xs text-destructive" role="alert">
              {tifluxErrorMessage(save.error)}
            </span>
          ) : null}
        </div>
      </form>
    </Section>
  );
}

// ── Sincronização (status + "Sincronizar agora") ────────────────────────────

function SyncSection({ integration }: { integration: TifluxIntegration }) {
  const qc = useQueryClient();
  const syncNow = useTifluxSyncNow();
  const { toast: errorToast, showToast: showError } = useTransientToast('error');
  const { toast: successToast, showToast: showSuccess } = useTransientToast('success');
  const { toast: infoToast, showToast: showInfo } = useTransientToast('info', 5000);

  // Poll pós-disparo: guardamos o lastFinishedAt do momento do 202 e refazemos
  // o GET a cada 3s até ele mudar (aí o lastResult na tela É o da rodada que
  // acabou) ou a janela de 60s estourar. Também pollamos com `syncRunning` do
  // GET (rodada em voo — ex.: cron) para o card se atualizar sozinho ao
  // terminar.
  const [polling, setPolling] = useState<{ baseline: string | null; startedAt: number } | null>(
    null
  );

  useEffect(() => {
    if (!polling && !integration.syncRunning) return;
    const interval = setInterval(() => {
      void qc.invalidateQueries({ queryKey: TIFLUX_KEY });
    }, SYNC_POLL_INTERVAL_MS);
    // A janela de 60s vale só para o poll do disparo manual — o poll por
    // syncRunning para naturalmente quando o GET volta com false.
    const timeout = polling
      ? setTimeout(() => {
          setPolling(null);
          showInfo(
            'A sincronização continua em segundo plano — o resultado aparece aqui ao concluir.'
          );
        }, SYNC_POLL_TIMEOUT_MS)
      : null;
    return () => {
      clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [polling, integration.syncRunning, qc, showInfo]);

  // Conclusão detectada: `lastFinishedAt` mudou em relação ao baseline do
  // disparo. Tem que ser ele: `lastSyncAt` é o LEASE, carimbado no COMEÇO da
  // rodada pelo claim, então mudaria logo no primeiro poll e o toast mostraria
  // o `lastResult` da rodada ANTERIOR. `syncRunning` também não serve sozinho —
  // é flag de processo e o GET pode cair noutra réplica. `lastFinishedAt` e
  // `lastResult` são gravados juntos, no fim, pelo finishRound.
  useEffect(() => {
    if (!polling || integration.lastFinishedAt === polling.baseline) return;
    setPolling(null);
    if (integration.lastResult && !integration.lastResult.ok) {
      showError('Sincronização concluída com erros — veja os detalhes abaixo.');
    } else {
      showSuccess('Sincronização concluída.');
    }
  }, [polling, integration.lastFinishedAt, integration.lastResult, showError, showSuccess]);

  const triggerSync = () => {
    syncNow.mutate(undefined, {
      onSuccess: () => setPolling({ baseline: integration.lastFinishedAt, startedAt: Date.now() }),
      // 409 sync_already_running (e demais falhas) viram toast pt-BR.
      onError: (err) => showError(tifluxErrorMessage(err)),
    });
  };

  const busy = syncNow.isPending || polling !== null || integration.syncRunning;
  const result = integration.lastResult;

  return (
    <Section
      title="Sincronização"
      description="Espelha mesas, clientes, tickets e comentários do TiFlux para o ERP (somente leitura)."
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={triggerSync}>
            {busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
            {busy ? 'Sincronizando…' : 'Sincronizar agora'}
          </Button>
          <span className="text-xs text-muted-foreground">
            {integration.lastSyncAt ? (
              <>
                Última sincronização{' '}
                <time dateTime={integration.lastSyncAt} title={integration.lastSyncAt}>
                  {tifluxRelativeTime(integration.lastSyncAt)}
                </time>
              </>
            ) : (
              'Nenhuma sincronização executada ainda.'
            )}
          </span>
          {result ? (
            <Badge variant={result.ok ? 'success' : 'destructive'}>
              {result.ok ? 'OK' : 'Erro'}
            </Badge>
          ) : null}
        </div>

        {result ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <SyncCounter label="Mesas" value={result.counts.desks} />
            <SyncCounter label="Clientes" value={result.counts.clients} />
            <SyncCounter label="Tickets" value={result.counts.tickets} />
            <SyncCounter label="Comentários" value={result.counts.comments} />
          </div>
        ) : null}

        {result ? (
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="tabular-nums">
              Duração: {(result.durationMs / 1000).toLocaleString('pt-BR', {
                maximumFractionDigits: 1,
              })}
              s
            </span>
            {/* spec 011: tickets que o TiFlux trouxe e o espelho ignorou por serem
                de mesa operada no ERP. Logo depois do deploy tem de ser 0 — as
                mesas nascem espelhadas; número aqui sem mesa virada é alarme. */}
            <span
              className="tabular-nums"
              title="Tickets de mesas operadas no ERP que o espelho não atualizou nesta rodada"
            >
              Ignorados (mesa do ERP): {result.counts.ticketsSkippedErpDesk.toLocaleString('pt-BR')}
            </span>
            {/* spec 024: tickets fora do espelho (cliente ou ticket desligado) que o
                TiFlux trouxe e o espelho não tocou — contador próprio */}
            <span
              className="tabular-nums"
              title="Tickets operados no ERP (fora do espelho) que o TiFlux trouxe nesta rodada e o espelho não tocou"
            >
              Fora do espelho: {result.counts.ticketsSkippedDetached.toLocaleString('pt-BR')}
            </span>
            {/* spec 026: ticket novo de cliente operado que saiu do espelho no
                primeiro detalhe completo */}
            <span
              className="tabular-nums"
              title="Tickets de clientes operados no ERP que chegaram pelo TiFlux e passaram a ser operados aqui nesta rodada"
            >
              Adotados: {result.counts.ticketsAdopted.toLocaleString('pt-BR')}
            </span>
            {/* spec 043: abertos aqui que não estão na lista de abertos de lá,
                conferidos pelo detalhe nesta rodada; o "a conferir" é a dívida
                que anda em fatias e tem de cair para perto de zero */}
            <span
              className="tabular-nums"
              title="Tickets abertos aqui que já não estão abertos no TiFlux, conferidos um a um nesta rodada: fechados e cancelados lá são encerrados aqui, sem automação nem aviso ao cliente"
            >
              Encerrados no TiFlux: {result.counts.reconciledClosed.toLocaleString('pt-BR')} fechados ·{' '}
              {result.counts.reconciledCanceled.toLocaleString('pt-BR')} cancelados ·{' '}
              {result.counts.reconciledDeleted.toLocaleString('pt-BR')} apagados lá ·{' '}
              {result.counts.reconciledStillOpen.toLocaleString('pt-BR')} ainda abertos ·{' '}
              {result.counts.pendingReconcile.toLocaleString('pt-BR')} a conferir
            </span>
            {result.errors.length > 0 ? <SyncErrorsDialog errors={result.errors} /> : null}
          </div>
        ) : null}
      </div>

      {errorToast}
      {successToast}
      {infoToast}
    </Section>
  );
}

// ── Clientes operados no ERP (spec 024, FR-003) ─────────────────────────────

/**
 * Desligar um cliente do espelho: todos os tickets ATUAIS dele passam a ser
 * operados no ERP, e o espelho não os toca mais (nem o cadastro dele). Tudo
 * por id — há nomes parecidos no cadastro, e o diálogo os mostra antes de
 * confirmar. Sem volta pela tela.
 *
 * Spec 026: o ticket NOVO desses clientes que chega pelo TiFlux é importado e
 * passa a ser operado aqui (adotado); um ciclo próprio, mais curto que o
 * espelho, traz comentários e apontamentos e marca como pendência o que a
 * equipe mudar lá (etapa, status, responsável, mesa). O admin decide na página
 * do cliente ("Puxar do TiFlux").
 */
function ErpClientsSection({ integration }: { integration: TifluxIntegration }) {
  const listQ = useQuery(tifluxErpClientsOptions());
  const [open, setOpen] = useState(false);
  const { toast: successToast, showToast: showSuccess } = useTransientToast('success');

  return (
    <Section
      title="Clientes operados no ERP"
      description="Os tickets destes clientes são editados aqui. Ticket novo que chegar pelo TiFlux é importado e passa a ser operado no ERP. O que a equipe mudar lá depois aparece como pendência na página do cliente, e um administrador escolhe o que puxar. O ERP nunca escreve no TiFlux."
    >
      <div className="space-y-3">
        <OperatedCycle integration={integration} />
        {listQ.isPending ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        ) : listQ.isError ? (
          <p className="text-xs text-destructive">{tifluxErrorMessage(listQ.error)}</p>
        ) : listQ.data.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nenhum cliente desligado do espelho.</p>
        ) : (
          <ul className="space-y-2">
            {listQ.data.map((c) => (
              <li key={c.client.id} className="rounded-md border px-3 py-2 text-sm">
                <p className="font-medium">
                  {c.client.name}{' '}
                  <span className="font-mono text-xs text-muted-foreground">
                    #{c.client.id}
                    {c.tifluxClientId !== null ? ` · TiFlux ${c.tifluxClientId}` : ''}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {c.ticketsCount.toLocaleString('pt-BR')} tickets ({c.openTicketsCount.toLocaleString('pt-BR')}{' '}
                  abertos) saíram do espelho em {new Date(c.detachedAt).toLocaleString('pt-BR')}
                  {c.detachedBy ? ` por ${c.detachedBy.name}` : ''}.{' '}
                  <Link
                    to="/clients/$clientId"
                    params={{ clientId: String(c.client.id) }}
                    className="text-primary hover:underline"
                  >
                    Abrir o cliente
                  </Link>
                </p>
              </li>
            ))}
          </ul>
        )}
        <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)}>
          <Unlink className="size-4" />
          Desligar um cliente do espelho
        </Button>
      </div>
      {open ? (
        <DetachClientDialog
          onClose={() => setOpen(false)}
          onDone={(name) => {
            setOpen(false);
            showSuccess(`${name}: tickets fora do espelho — agora operados no ERP.`);
          }}
        />
      ) : null}
      {successToast}
    </Section>
  );
}

function DetachClientDialog({ onClose, onDone }: { onClose: () => void; onDone: (name: string) => void }) {
  const [client, setClient] = useState<ClientRef | null>(null);
  const previewQ = useQuery({ ...tifluxErpClientPreviewOptions(client?.id ?? 0), enabled: client !== null });
  const detach = useDetachTifluxErpClient();
  const preview = previewQ.data;

  return (
    <Dialog open onOpenChange={(next) => (next ? null : onClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Desligar um cliente do espelho do TiFlux</DialogTitle>
          <DialogDescription>
            Todos os tickets atuais do cliente passam a ser operados no ERP. O espelho não os atualiza mais, nem o
            cadastro do cliente; as cópias no TiFlux ficam com a equipe de lá. Não há volta pela tela.
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0 space-y-3">
          <ClientPicker selected={client} onSelect={setClient} />
          {client && previewQ.isPending ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
          {previewQ.isError ? <p className="text-xs text-destructive">{tifluxErrorMessage(previewQ.error)}</p> : null}
          {preview ? (
            <div className="space-y-2 rounded-md border px-3 py-2 text-sm">
              <p>
                <span className="font-medium">{preview.client.name}</span>{' '}
                <span className="font-mono text-xs text-muted-foreground">
                  id #{preview.client.id} · TiFlux {preview.tifluxClientId ?? 'sem vínculo'}
                </span>
              </p>
              <p className="text-xs text-muted-foreground">
                {preview.tickets.toLocaleString('pt-BR')} tickets, {preview.openTickets.toLocaleString('pt-BR')}{' '}
                abertos — todos saem do espelho.
              </p>
              {preview.similar.length > 0 ? (
                <p className="flex items-start gap-1.5 text-xs text-[#D48806]">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                  <span>
                    Há clientes com nome parecido — confira o id:{' '}
                    {preview.similar.map((s) => `${s.name} (#${s.id})`).join(', ')}.
                  </span>
                </p>
              ) : null}
              {preview.alreadyDetached ? (
                <p className="text-xs text-muted-foreground">Este cliente já é operado no ERP.</p>
              ) : null}
            </div>
          ) : null}
          {detach.isError ? <p className="text-xs text-destructive">{tifluxErrorMessage(detach.error)}</p> : null}
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Voltar
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!preview || preview.alreadyDetached || detach.isPending}
            onClick={() => {
              if (!preview) return;
              detach.mutate(preview.client.id, { onSuccess: (done) => onDone(done.client.name) });
            }}
          >
            {detach.isPending ? 'Desligando…' : preview ? `Desligar #${preview.client.id}` : 'Desligar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * O ciclo dos clientes operados (spec 026, FR-002): intervalo próprio (padrão
 * 10 min, o PUT aceita 5..1440) e a última volta. O espelho dos demais segue no
 * intervalo da Conexão.
 */
function OperatedCycle({ integration }: { integration: TifluxIntegration }) {
  const save = useUpdateTifluxIntegration();
  const options: number[] = [...TIFLUX_OPERATED_INTERVAL_OPTIONS];
  if (!options.includes(integration.operatedIntervalMinutes)) options.push(integration.operatedIntervalMinutes);
  options.sort((a, b) => a - b);
  const result = integration.operatedLastResult;

  return (
    <div className="space-y-2 rounded-md border bg-background/60 px-3 py-2">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="tiflux-operated-interval">Ciclo dos clientes operados</Label>
          <Select
            id="tiflux-operated-interval"
            value={String(integration.operatedIntervalMinutes)}
            disabled={save.isPending}
            onChange={(e) => save.mutate({ operatedIntervalMinutes: Number(e.target.value) })}
          >
            {options.map((min) => (
              <option key={min} value={min}>
                A cada {min} minutos
              </option>
            ))}
          </Select>
        </div>
        <span className="pb-2 text-xs text-muted-foreground">
          {integration.operatedLastFinishedAt ? (
            <>
              Última volta{' '}
              <time dateTime={integration.operatedLastFinishedAt} title={integration.operatedLastFinishedAt}>
                {tifluxRelativeTime(integration.operatedLastFinishedAt)}
              </time>
              {result
                ? ` · ${[
                    count(result.counts.created, 'novo', 'novos'),
                    count(result.counts.adopted, 'adotado', 'adotados'),
                    count(result.counts.comments, 'comentário', 'comentários'),
                    count(result.counts.pendingsOpened, 'pendência', 'pendências'),
                    // ADR-013: só quando houve — o caso raro, que pede decisão
                    ...(result.counts.appointmentPendingsOpened > 0
                      ? [
                          count(
                            result.counts.appointmentPendingsOpened,
                            'pendência de apontamento',
                            'pendências de apontamento'
                          ),
                        ]
                      : []),
                  ].join(', ')} · ${count(result.counts.calls, 'chamada', 'chamadas')}`
                : null}
            </>
          ) : (
            'O ciclo ainda não rodou.'
          )}
        </span>
        {result ? (
          <Badge variant={result.ok ? 'success' : 'destructive'} className="mb-2">
            {result.ok ? 'OK' : 'Erro'}
          </Badge>
        ) : null}
        {result && result.errors.length > 0 ? (
          <span className="mb-1">
            <SyncErrorsDialog errors={result.errors} />
          </span>
        ) : null}
      </div>
      {save.error ? (
        <p className="text-xs text-destructive" role="alert">
          {tifluxErrorMessage(save.error)}
        </p>
      ) : null}
    </div>
  );
}

// ── Ocultar tickets espelhados (spec 024, FR-006) ───────────────────────────

/**
 * Ligada, ticket espelhado de cliente que NÃO é operado no ERP some de toda a
 * tela — lista, busca, contagens, Hoje, portal, relatórios… — para todos,
 * admin inclusive; o detalhe dele não abre. O espelho continua rodando:
 * desligar volta a mostrar tudo.
 */
function HideMirroredSection({ integration }: { integration: TifluxIntegration }) {
  const save = useUpdateTifluxIntegration();
  const qc = useQueryClient();
  return (
    <Section
      title="Tickets espelhados"
      description="Esconder do ERP os tickets espelhados do TiFlux de clientes que não são operados aqui. Vale para todos, administradores inclusive; o espelho segue rodando."
    >
      <div className="space-y-2">
        <span className="flex items-center gap-2">
          <Switch
            id="tiflux-hide-mirrored"
            aria-label="Ocultar tickets espelhados"
            checked={integration.hideMirrored}
            disabled={save.isPending}
            onCheckedChange={(hideMirrored) =>
              save.mutate(
                { hideMirrored },
                // tudo que lista ticket muda de uma vez
                { onSuccess: () => void qc.invalidateQueries({ queryKey: ['erp'] }) }
              )
            }
          />
          <Label htmlFor="tiflux-hide-mirrored" className="cursor-pointer">
            Ocultar tickets espelhados
          </Label>
        </span>
        {save.error ? (
          <p className="text-xs text-destructive" role="alert">
            {tifluxErrorMessage(save.error)}
          </p>
        ) : null}
      </div>
    </Section>
  );
}

/** "1 novo", "2 novos" — número pt-BR com o singular certo */
function count(n: number, one: string, many: string): string {
  return `${n.toLocaleString('pt-BR')} ${n === 1 ? one : many}`;
}

function SyncCounter({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border bg-background/60 px-3 py-2">
      <p className="text-lg leading-6 font-semibold tabular-nums">
        {value.toLocaleString('pt-BR')}
      </p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

/** Lista dos erros da última sincronização num dialog expandível. */
function SyncErrorsDialog({ errors }: { errors: TifluxSyncError[] }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          Ver {errors.length} {errors.length === 1 ? 'erro' : 'erros'}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Erros da última sincronização</DialogTitle>
          <DialogDescription>
            Registros do TiFlux que não puderam ser espelhados nesta execução.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-2">
          {errors.map((err, i) => (
            <li key={i} className="rounded-md border px-3 py-2 text-sm">
              <p className="text-xs font-medium text-muted-foreground">
                {TIFLUX_ENTITY_LABELS[err.entity] ?? err.entity}
                {err.id !== undefined ? (
                  <span className="font-mono"> · #{err.id}</span>
                ) : null}
              </p>
              <p className="mt-0.5">{err.message}</p>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
